import React, { useEffect, useMemo, useState } from 'react'
import SlideBase from './SlideBase'
import WrappedVideoBackground from '../WrappedVideoBackground'
import Caption from 'LuxKit/Typography/Caption'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import FadeBackground from '../FadeBackground'
import { useWrappedStorySelector } from 'contexts/Wrapped'
import { WRAPPED_SLIDES } from '../../config/slides'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'

// Get video config for this slide (static, doesn't depend on runtime data)
const SLIDE_VIDEO = WRAPPED_SLIDES.find(s => s.id === 'nights')?.video
const SLIDE_ID = 'nights' as const

function NightsSpentSlide() {
  // Use granular selectors to minimize re-renders
  const data = useWrappedStorySelector(state => state.data)
  const totalsData = useWrappedStorySelector(state => state.totalsData)
  const isVideoReady = useWrappedStorySelector(state => state.isVideoReady)
  const isCurrentSlide = useWrappedStorySelector(state => state.currentSlide?.id === SLIDE_ID)
  const [animate, setAnimate] = useState(!SLIDE_VIDEO) // If no video, animate immediately

  // Totals-only mode is determined by not having user wrapped data
  const isTotalsOnlyMode = !data

  // Start animation when video is ready (only when this is the current slide)
  useEffect(() => {
    if (SLIDE_VIDEO && isVideoReady && isCurrentSlide) {
      setAnimate(true)
    }
  }, [isVideoReady, isCurrentSlide])

  // Memoize background to prevent recreation
  const videoBackground = useMemo(() => {
    if (!SLIDE_VIDEO) return undefined
    return <WrappedVideoBackground video={SLIDE_VIDEO} isActive={isCurrentSlide} />
  }, [isCurrentSlide])

  // Need either user data or totals data to render
  if (!data && !totalsData) return null

  const userNights = data?.stats.totalNights ?? 0
  const useTotalsFallback = isTotalsOnlyMode || userNights <= 0
  const totalNights = totalsData?.stats.totalNights ?? 0

  const title = useTotalsFallback
    ? 'In 2025, Luxury Escapes travellers spent a total of'
    : 'Last year, you spent a total of'
  const nightsDisplayed = useTotalsFallback ? totalNights : userNights
  const useCompactFormat = nightsDisplayed >= 1_000_000

  return (
    <SlideBase background={videoBackground}>
      {animate &&
        <FadeBackground>
          <Group direction="vertical" grow verticalAlign="end" horizontalAlign="start" gap={12}>
            <Group direction="vertical" gap={4}>
              <Caption colour="highlight-primary" variant="small" format="uppercase">Total Nights Spent</Caption>
              <Heading variant="heading5"><b>{title}</b></Heading>
            </Group>
            <Heading variant="heading1"><i><FormatDecimal value={nightsDisplayed} notation={useCompactFormat ? 'compact' : 'standard'} compactDisplay="long" maximumFractionDigits={1} /> nights away</i></Heading>
          </Group>
        </FadeBackground>}
    </SlideBase>
  )
}

export default React.memo(NightsSpentSlide)
